import React, { useEffect, useState } from 'react'
import { useAuth } from '../App'
import { getWeeklyStats, getMonthlyCalendar, getUserSessions } from '../lib/supabase'
import { format, subDays, isSameDay } from 'date-fns'
import { el } from 'date-fns/locale'

export default function Stats() {
  const { user } = useAuth()
  const [weekly, setWeekly] = useState([])
  const [calendar, setCalendar] = useState([])
  const [sessions, setSessions] = useState([])

  useEffect(() => {
    if (!user) return
    getWeeklyStats(user.id).then(setWeekly)
    getMonthlyCalendar(user.id).then(setCalendar)
    getUserSessions(user.id, 10).then(setSessions)
  }, [user])

  const totalWeekMin = weekly.reduce((a, s) => a + (s.total_minutes || 0), 0)
  const totalAllMin = user?.total_minutes || 0

  // Section breakdown
  const sectionMap = {}
  weekly.forEach(s => {
    (s.sections_log || []).forEach(sl => {
      sectionMap[sl.section_id] = (sectionMap[sl.section_id] || 0) + (sl.minutes || 0)
    })
  })
  const totalSectionMin = Object.values(sectionMap).reduce((a, b) => a + b, 0) || 1
  const SECTIONS = [
    { id: 'tech',  label: 'Τεχνική',          icon: '🎹', color: 'var(--blue)' },
    { id: 'comp',  label: 'Comping',           icon: '🎼', color: 'var(--purple)' },
    { id: 'impro', label: 'Αυτοσχεδιασμός',   icon: '🎵', color: 'var(--amber)' },
    { id: 'rep',   label: 'Ρεπερτόριο',        icon: '🎶', color: 'var(--green)' },
  ]

  // Calendar: last 30 days
  const calDays = Array.from({ length: 30 }, (_, i) => subDays(new Date(), 29 - i))
  const getMinutesForDay = (day) => {
    const match = calendar.find(s => isSameDay(new Date(s.completed_at), day))
    return match ? match.total_minutes : 0
  }
  const getIntensity = (min) => {
    if (min === 0) return 0
    if (min < 15) return 1
    if (min < 30) return 2
    return 3
  }
  const intensityColor = ['var(--border)', 'rgba(59,125,255,0.25)', 'rgba(59,125,255,0.55)', 'var(--blue)']

  return (
    <div style={{ padding: '20px 16px' }}>
      <div className="fade-up" style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Η πρόοδός σου</div>
        <div className="page-title">Stats <span style={{ color: 'var(--blue)' }}>μου</span></div>
      </div>

      {/* Summary cards */}
      <div className="fade-up fade-up-1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
        {[
          { label: 'Αυτή την εβδομάδα', value: `${totalWeekMin}λ`, sub: `${weekly.length} sessions` },
          { label: 'Streak', value: `${user?.current_streak || 0}🔥`, sub: `Καλύτερο: ${user?.longest_streak || 0}` },
          { label: 'Σύνολο ώρες', value: `${Math.round(totalAllMin / 60)}h`, sub: `${totalAllMin} λεπτά` },
          { label: 'Εβδομαδιαίος στόχος', value: weekly.length >= 5 ? '✅' : `${weekly.length}/5`, sub: weekly.length >= 5 ? 'Εκπληρώθηκε!' : 'sessions' },
        ].map((c, i) => (
          <div key={i} className="card" style={{ background: 'var(--navy-light)' }}>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>{c.label}</div>
            <div style={{ fontSize: 26, fontWeight: 500, marginBottom: 2 }}>{c.value}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Calendar heatmap */}
      <div className="fade-up fade-up-2" style={{ marginBottom: 20 }}>
        <div className="section-label">30 μέρες μελέτης</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 5 }}>
          {calDays.map((day, i) => {
            const min = getMinutesForDay(day)
            const intensity = getIntensity(min)
            const isToday = isSameDay(day, new Date())
            return (
              <div key={i} title={`${format(day, 'd MMM', { locale: el })}: ${min}λ`}
                style={{
                  aspectRatio: '1', borderRadius: 6,
                  background: intensityColor[intensity],
                  border: isToday ? '1.5px solid var(--blue)' : '1px solid transparent',
                  transition: 'background 0.2s',
                }} />
            )
          })}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
          <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>Λίγο</span>
          {[0,1,2,3].map(i => <div key={i} style={{ width: 10, height: 10, borderRadius: 3, background: intensityColor[i] }} />)}
          <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>Πολύ</span>
        </div>
      </div>

      {/* Section breakdown */}
      <div className="fade-up fade-up-3" style={{ marginBottom: 20 }}>
        <div className="section-label">Breakdown ενοτήτων (εβδομάδα)</div>
        <div className="card">
          {SECTIONS.map(s => {
            const min = sectionMap[s.id] || 0
            const pct = Math.round((min / totalSectionMin) * 100)
            return (
              <div key={s.id} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                  <span>{s.icon} {s.label}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{min > 0 ? `${min}λ` : '—'}</span>
                </div>
                <div style={{ height: 6, background: 'var(--border)', borderRadius: 3 }}>
                  <div style={{ width: `${pct}%`, height: 6, background: s.color, borderRadius: 3, transition: 'width 0.8s ease' }} />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Recent sessions */}
      <div className="fade-up fade-up-4">
        <div className="section-label">Πρόσφατα sessions</div>
        {sessions.map(s => (
          <div key={s.id} className="card" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--blue-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🎹</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{s.programs?.title || 'Free session'}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {s.completed_at ? format(new Date(s.completed_at), 'EEEE d MMM', { locale: el }) : ''}
              </div>
            </div>
            <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--blue)' }}>{s.total_minutes}λ</div>
          </div>
        ))}
      </div>
    </div>
  )
}
